import { useState } from 'react';
import {
  X,
  Gavel,
  Building2,
  Package,
  Clock,
  CheckCircle2,
  XCircle,
  Printer,
  Download,
  FileText,
  Trophy,
} from 'lucide-react';
import { cn } from '@/lib/classnames';
import { Button } from '@/components/ui/button';
import type { Bid } from '../../types';
import {
  BID_STATUS_LABELS, BID_STATUS_COLORS 
} from '../../types';
import type {
  TabValue, Tab 
} from './types';
import { DetailsTab } from './DetailsTab';
import { ItemsTab } from './ItemsTab';
import { TimelineTab } from './TimelineTab';

interface BidDetailModalProps {
  readonly bid: Bid;
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly onAccept?: (bidId: string) => void;
  readonly onReject?: (bidId: string) => void;
  readonly onCreatePO?: (bidId: string) => void;
}

const TABS: Tab[] = [
  {
    value: 'details',
    label: 'Details',
    icon: <FileText className="h-4 w-4" />,
  },
  {
    value: 'items',
    label: 'Line Items',
    icon: <Package className="h-4 w-4" />,
  },
  {
    value: 'timeline',
    label: 'Timeline',
    icon: <Clock className="h-4 w-4" />,
  },
];

export function BidDetailModal({
  bid,
  isOpen,
  onClose,
  onAccept,
  onReject,
  onCreatePO,
}: BidDetailModalProps) {
  const [activeTab, setActiveTab] = useState<TabValue>('details');

  if (!isOpen) return null;

  const canReview = bid.status === 'submitted' || bid.status === 'under_review';
  const isAccepted = bid.status === 'accepted';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative z-50 flex max-h-[90vh] w-full max-w-4xl flex-col rounded-lg bg-white shadow-xl">
        <ModalHeader bid={bid} onClose={onClose} />
        <TabNavigation tabs={TABS} activeTab={activeTab} onTabChange={setActiveTab} />
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'details' && <DetailsTab bid={bid} />}
          {activeTab === 'items' && <ItemsTab bid={bid} />}
          {activeTab === 'timeline' && <TimelineTab bid={bid} />}
        </div>
        <ModalFooter
          bid={bid}
          canReview={canReview}
          isAccepted={isAccepted}
          onAccept={onAccept}
          onReject={onReject}
          onCreatePO={onCreatePO}
        />
      </div>
    </div>
  );
}

function ModalHeader({
  bid,
  onClose,
}: {
  readonly bid: Bid;
  readonly onClose: () => void;
}) {
  return (
    <div className="flex items-center justify-between border-b px-6 py-4">
      <div className="flex items-center gap-4">
        <div className={cn('rounded-lg p-3', bid.isLowestBid ? 'bg-green-100' : 'bg-blue-100')}>
          {bid.isLowestBid ? (
            <Trophy className="h-6 w-6 text-green-600" />
          ) : (
            <Gavel className="h-6 w-6 text-blue-600" />
          )}
        </div>
        <div>
          <h2 className="text-xl font-semibold text-gray-900">{bid.supplierName}</h2>
          <p className="flex items-center gap-2 text-sm text-gray-500">
            <Building2 className="h-4 w-4" />
            {bid.eventTitle}
          </p>
        </div>
        {bid.rank && (
          <span className="rounded-full bg-gray-100 px-3 py-1 text-sm font-medium text-gray-800">
            Rank #{bid.rank}
          </span>
        )}
        <span
          className={cn('rounded-full px-3 py-1 text-sm font-medium', BID_STATUS_COLORS[bid.status])}
        >
          {BID_STATUS_LABELS[bid.status]}
        </span>
      </div>
      <button
        type="button"
        onClick={onClose}
        className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
      >
        <X className="h-5 w-5" />
      </button>
    </div>
  );
}

function TabNavigation({
  tabs,
  activeTab,
  onTabChange,
}: {
  readonly tabs: Tab[];
  readonly activeTab: TabValue;
  readonly onTabChange: (tab: TabValue) => void;
}) {
  return (
    <div className="flex gap-1 border-b px-6">
      {tabs.map((tab) => (
        <button
          key={tab.value}
          type="button"
          onClick={() => onTabChange(tab.value)}
          className={cn(
            'flex items-center gap-2 border-b-2 px-4 py-3 text-sm font-medium transition-colors',
            activeTab === tab.value
              ? 'border-primary text-primary'
              : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
          )}
        >
          {tab.icon}
          {tab.label}
        </button>
      ))}
    </div>
  );
}

interface ModalFooterProps {
  readonly bid: Bid;
  readonly canReview: boolean;
  readonly isAccepted: boolean;
  readonly onAccept?: (bidId: string) => void;
  readonly onReject?: (bidId: string) => void;
  readonly onCreatePO?: (bidId: string) => void;
}

function ModalFooter({
  bid,
  canReview,
  isAccepted,
  onAccept,
  onReject,
  onCreatePO,
}: ModalFooterProps) {
  return (
    <div className="flex items-center justify-between border-t px-6 py-4">
      <div className="flex gap-2">
        <Button variant="outline" size="sm">
          <Printer className="mr-2 h-4 w-4" />
          Print
        </Button>
        <Button variant="outline" size="sm">
          <Download className="mr-2 h-4 w-4" />
          Export PDF
        </Button>
      </div>
      <div className="flex gap-2">
        {canReview && onReject && (
          <Button
            variant="outline"
            size="sm"
            className="text-red-600 hover:text-red-700"
            onClick={() => onReject(bid.bidId)}
          >
            <XCircle className="mr-2 h-4 w-4" />
            Reject
          </Button>
        )}
        {canReview && onAccept && (
          <Button size="sm" onClick={() => onAccept(bid.bidId)}>
            <CheckCircle2 className="mr-2 h-4 w-4" />
            Accept Bid
          </Button>
        )}
        {isAccepted && onCreatePO && (
          <Button size="sm" onClick={() => onCreatePO(bid.bidId)}>
            <FileText className="mr-2 h-4 w-4" />
            Create Purchase Order
          </Button>
        )}
      </div>
    </div>
  );
}
